import { useCallback, useMemo, useState } from 'react';
import { selectedStructureId } from '@/state';
import { ReportSubmitState } from '@/types';
import {
  ReportActionStatus,
  ReportInternalType,
  ReportStatus,
  useCreateNewReportMutation,
  useGetAllDraftReportsQuery,
  useUpdateReportByPointOfInterestsMutation,
} from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useRouter } from '@/hooks';

type Props = {
  selectedPointOfInterestIds: string[];
  handleClose: () => void;
  internalType: ReportInternalType;
  reportCreated?: () => void;
  componentIds?: string[];
};

type SubmittingReportState = 'Loading' | 'Success' | undefined;

const reportNameRegex = /^[\w\s(){}[\]?/&+=.\\-]{5,200}$/;

export const useReportFormLogic = ({
  selectedPointOfInterestIds,
  handleClose,
  internalType,
  reportCreated,
  componentIds,
}: Props) => {
  const router = useRouter();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useAtomValue(selectedStructureId)!;

  const [reportState, setReportState] = useState<ReportSubmitState>('create');
  const [reportName, setReportName] = useState('');
  const [workpackNumber, setWorkpackNumber] = useState('');
  const [actionStatus, setActionStatus] = useState<ReportActionStatus>(ReportActionStatus.Backlog);
  const [dueDate, setDueDate] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [reportId, setReportId] = useState('');
  const [submittingReportState, setSubmittingReportState] = useState<SubmittingReportState>();

  const [createNewReportMutation] = useCreateNewReportMutation();
  const [updateReportByPointOfInterestsMutation] = useUpdateReportByPointOfInterestsMutation();

  const getAllDraftReportsQueryResult = useGetAllDraftReportsQuery({
    variables: {
      input: {
        structureId,
        status: ReportStatus.Draft,
        reportType: internalType,
      },
    },
  });

  const getReportNameError = (name: string): string | undefined => {
    if (name.length < 5) return 'Report name must be at least 5 characters.';
    if (name.length > 200) return 'Report name must be no more than 200 characters.';
    if (!reportNameRegex.test(name)) return 'Report name contains invalid characters.';
    return undefined;
  };

  const handleRadioChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (['create', 'edit'].includes(value)) setReportState(value as ReportSubmitState);
  }, []);

  const handleReportNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(event.target.value);
  }, []);

  const handleWorkpackNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkpackNumber(event.target.value);
  }, []);

  const handleReportDueDateChange = useCallback((value: unknown) => {
    if (!value) return;
    setDueDate(value.toString());
  }, []);

  const handleReportActionStatusChange = useCallback((_: unknown, value: string | null) => {
    if (!value) return;
    setActionStatus(
      Object.values(ReportActionStatus).find((status) => status === value) ??
        ReportActionStatus.Backlog
    );
  }, []);

  const handleReportIdChanged = useCallback(
    (_: unknown, report: { label: string; id: string } | null) => {
      if (!report) return;
      setReportId(report.id);
    },
    []
  );

  const handleCreateReport = useCallback(async () => {
    try {
      const newReport = await createNewReportMutation({
        variables: {
          input: {
            internalType,
            pointOfInterestIds: selectedPointOfInterestIds,
            name: reportName,
            structureId,
            actionStatus,
            ...(dueDate ? { dueDate } : {}),
            ...(workpackNumber ? { workpackNumber } : {}),
            ...(internalType === ReportInternalType.Component && { componentIds }),
          },
        },
      });
      const newReportId = newReport?.data?.createNewReport?.id;
      setSubmittingReportState('Success');
      showSnackBar('Report has been created successfully', 'success');
      setTimeout(() => {
        handleClose();
        const url = `/analysis/${structureId}/reports/${newReportId ?? ''}`;
        internalType === ReportInternalType.Component
          ? window.open(url, '_blank')
          : router.push(url);
      }, 1000);
    } catch {
      showSnackBar(`Error creating report ${reportName}`, 'error');
    }
  }, [
    actionStatus,
    componentIds,
    createNewReportMutation,
    dueDate,
    handleClose,
    internalType,
    reportName,
    router,
    selectedPointOfInterestIds,
    showSnackBar,
    structureId,
    workpackNumber,
  ]);

  const handleAddPOIToReport = useCallback(async () => {
    try {
      await updateReportByPointOfInterestsMutation({
        variables: {
          input: {
            reportId,
            pointOfInterestIds: selectedPointOfInterestIds,
          },
        },
      });
      setSubmittingReportState('Success');
      setTimeout(() => {
        handleClose();
        reportCreated?.();
      }, 3000);
    } catch {
      showSnackBar('Error while updating report', 'error');
    }
  }, [
    handleClose,
    reportCreated,
    reportId,
    selectedPointOfInterestIds,
    showSnackBar,
    updateReportByPointOfInterestsMutation,
  ]);

  const handleAddOrCreateReport = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      setSubmittingReportState('Loading');
      if (reportState === 'create') {
        handleCreateReport();
      } else {
        handleAddPOIToReport();
      }
    },
    [handleCreateReport, handleAddPOIToReport, reportState]
  );

  const draftReports = useMemo(() => {
    return getAllDraftReportsQueryResult.data?.allReports ?? [];
  }, [getAllDraftReportsQueryResult.data?.allReports]);

  const mappedDraftReports = useMemo(() => {
    return draftReports.map((report) => ({
      label: report?.name ?? '',
      id: report?.id ?? '',
    }));
  }, [draftReports]);

  const isInValidReportEntry = useMemo(() => {
    if (reportState === 'edit' && !reportId) return true;
    if (reportState === 'create' && !reportNameRegex.test(reportName)) return true;
    return false;
  }, [reportId, reportName, reportState]);

  return {
    reportState,
    reportName,
    workpackNumber,
    actionStatus,
    dueDate,
    isFocused,
    reportId,
    submittingReportState,
    draftReports,
    mappedDraftReports,
    isInValidReportEntry,
    getReportNameError,
    handleRadioChanged,
    handleReportNameChange,
    handleWorkpackNumberChange,
    handleReportDueDateChange,
    handleReportActionStatusChange,
    handleReportIdChanged,
    handleAddOrCreateReport,
    setIsFocused,
  };
};
